import { useState, useCallback, useRef, useEffect } from 'react';
import * as drf from '../../static/js/DataRequestFunctions';
import * as turf from '@turf/turf';

export const useHexagonData = (selectedFeatureId, selectedYear, month) => {
    const [pickedAreaData, setPickedAreaData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [selectedObjectIds, setSelectedObjectIds] = useState([]);
    const [clusterData, setClusterData] = useState([]);

    const pickedAreaDataRef = useRef({});
    const selectedObjectIdsRef = useRef([]);
    const clusterDataRef = useRef([]);  // ← добавлен ref для clusterData

    // Синхронизируем ref при изменении clusterData
    useEffect(() => {
        clusterDataRef.current = clusterData;
    }, [clusterData]);

    const updatePickedAreaData = (data) => {
        pickedAreaDataRef.current = data;
        setPickedAreaData(data);
    };

    const updateSelectedObjectIds = (ids) => {
        selectedObjectIdsRef.current = ids;
        setSelectedObjectIds(ids);
    };

    const lastAggKeyRef = useRef("");

    const loadAggregatedData = useCallback(async (hexagons) => {
        if (!hexagons?.length || !selectedYear || !month) {
            lastAggKeyRef.current = "";
            updatePickedAreaData({});
            setClusterData([]);
            clusterDataRef.current = [];
            return;
        }

        const ids = hexagons.map(h => h.properties.object_id).join(",");
        const aggKey = `${ids}-${selectedYear}-${month}`;
        if (lastAggKeyRef.current === aggKey) return;
        lastAggKeyRef.current = aggKey;
        // #region agent log
        fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'lang-fix',hypothesisId:'H2',location:'useHexagonData.js:loadAggregatedData',message:'selection fetch',data:{aggKey,count:hexagons.length},timestamp:Date.now()})}).catch(()=>{});
        // #endregion

        setIsLoading(true);
        try {
            const clusters = buildClusters(hexagons);

            const clusterPromises = clusters.map(async (cluster) => {
                const ids = cluster.map(h => h.properties.object_id);
                const raw = await drf.fetchValuesOfSelectedObjects(selectedYear, ids, month);
                return { cluster, values: raw };
            });

            const results = await Promise.all(clusterPromises);
            setClusterData(results);
            clusterDataRef.current = results;  // обновляем ref

            const total = {};
            results.forEach(({ values }) => {
                Object.keys(values).forEach(fid => {
                    total[fid] = (total[fid] || 0) + values[fid];
                });
            });
            updatePickedAreaData(total);
        } catch (error) {
            console.error('[useHexagonData] Error loading aggregated data:', error);
            updatePickedAreaData({});
            setClusterData([]);
            clusterDataRef.current = [];
        } finally {
            setIsLoading(false);
        }
    }, [selectedYear, month]);

    const handleHexagonSelection = useCallback((selectedData) => {
        lastAggKeyRef.current = "";
        pickedAreaDataRef.current = {};
        selectedObjectIdsRef.current = [];
        clusterDataRef.current = [];

        setSelectedObjectIds([]);
        setPickedAreaData({});
        setClusterData([]);
        setIsLoading(false);

        if (!selectedData || (Array.isArray(selectedData) && selectedData.length === 0)) {
            return;
        }

        const hexagonsArray = Array.isArray(selectedData) ? selectedData : [selectedData];
        const ids = hexagonsArray.map(h => h.properties.object_id);
        selectedObjectIdsRef.current = ids;
        setSelectedObjectIds(ids);
        loadAggregatedData(hexagonsArray);
    }, [loadAggregatedData]);

    const reloadCurrentSelection = useCallback((mapViews) => {
        const ids = selectedObjectIdsRef.current;
        if (!ids.length) {
            updatePickedAreaData({});
            setClusterData([]);
            clusterDataRef.current = [];
            return;
        }
        if (!mapViews?.length) return;

        const selectedHexagons = mapViews.filter(f =>
            ids.some(id => id == f.properties.object_id)
        );
        if (selectedHexagons.length > 0) {
            loadAggregatedData(selectedHexagons);
        } else {
            updatePickedAreaData({});
            setClusterData([]);
            clusterDataRef.current = [];
        }
    }, [loadAggregatedData]);

    const generateLabels = useCallback(() => {
        if (!selectedFeatureId || !clusterData.length) {
            return [];
        }
        const labels = [];
        clusterData.forEach(({ cluster, values }, idx) => {
            const value = values[String(selectedFeatureId)] ?? values[selectedFeatureId];
            if (value == null) return;
            try {
            const features = cluster.map(f => turf.polygon(f.geometry.coordinates));
                const center = turf.centroid(turf.featureCollection(features));
                labels.push({
                    type: 'Feature',
                    geometry: center.geometry,
                    properties: {
                        object_id: `cluster_${cluster[0].properties.object_id}`,
                        label: formatValue(value)
                    }
                });
            } catch (e) {
                console.warn('Cluster centroid error', e);
            }
        });
        return labels;
    }, [selectedFeatureId, clusterData]);

    return {
        pickedAreaData,
        selectedObjectIds,
        isLoading,
        handleHexagonSelection,
        reloadCurrentSelection,
        generateLabels,
        clusterData,
        setSelectedObjectIds,
        setPickedAreaData,
    };
};

function buildClusters(hexagons) {
    const visited = new Set();
    const clusters = [];

    for (let i = 0; i < hexagons.length; i++) {
        const id = hexagons[i].properties.object_id;
        if (visited.has(id)) continue;

        const cluster = [];
        const queue = [i];
        visited.add(id);

        while (queue.length > 0) {
            const idx = queue.shift();
            const hex = hexagons[idx];
            cluster.push(hex);

            for (let j = 0; j < hexagons.length; j++) {
                const neighborId = hexagons[j].properties.object_id;
                if (visited.has(neighborId)) continue;

                try {
                    const a = turf.polygon(hex.geometry.coordinates);
                    const b = turf.polygon(hexagons[j].geometry.coordinates);
                    if (turf.booleanIntersects(a, b)) {
                        visited.add(neighborId);
                        queue.push(j);
                    }
                } catch (e) {}
            }
        }
        clusters.push(cluster);
    }
    return clusters;
}

function formatValue(value) {
    if (value == null) return '—';
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return String(Math.round(value * 100) / 100);
}